import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Mail, PenLine } from "lucide-react";
import { authorBySlug, authorSchema, resolveAuthor, type Author } from "@/lib/authors";
import { loadPosts, formatDate, readingMinutes, CATEGORY_LABELS, type Post } from "@/lib/posts";

export const Route = createFileRoute("/nyheter/skribent/$slug")({
  loader: async ({ params }) => {
    const author = authorBySlug(params.slug);
    if (!author) throw notFound();
    const posts = await loadPosts();
    const written = posts
      .filter((p) => resolveAuthor(p.author).slug === author.slug)
      .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
    return { author, written };
  },
  head: ({ loaderData, params }) => {
    const author = loaderData?.author;
    if (!author) return { meta: [{ title: "Skribent hittades inte – Skolvyn" }] };
    const url = `https://skolvyn.se/nyheter/skribent/${params.slug}`;
    const title = `${author.name} – ${author.role} | Skolvyn`;
    const description = author.bio.slice(0, 155);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            ...authorSchema(author),
            knowsAbout: author.beats,
            email: author.email,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Start", item: "https://skolvyn.se/" },
              { "@type": "ListItem", position: 2, name: "Nyheter", item: "https://skolvyn.se/nyheter" },
              { "@type": "ListItem", position: 3, name: author.name, item: url },
            ],
          }),
        },
      ],

    };
  },
  notFoundComponent: () => (
    <div className="measure-content px-gutter py-section text-center">
      <h1 className="text-h1 font-display text-ink">Skribenten hittades inte</h1>
      <Link to="/nyheter" className="mt-stack-2 inline-block text-green-500 underline">
        Till nyheterna
      </Link>
    </div>
  ),
  component: AuthorPage,
});

function AuthorPage() {
  const { author, written } = Route.useLoaderData() as { author: Author; written: Post[] };
  return (
    <div className="measure-content px-gutter py-section">
      <nav aria-label="Brödsmulor" className="text-label text-ink-muted">
        <Link to="/nyheter" className="hover:text-green-700">
          Nyheter
        </Link>
        <span aria-hidden> / </span>
        <span>{author.name}</span>
      </nav>

      <header className="mt-stack-2 flex flex-col gap-stack-2 sm:flex-row sm:items-start">
        <div
          aria-hidden
          className="flex h-20 w-20 shrink-0 items-center justify-center rounded-card border border-line bg-green-100 font-display text-h2 text-green-700"
        >
          {author.name.slice(0, 1)}
        </div>
        <div>
          <h1 className="text-h1 font-display text-ink">{author.name}</h1>
          <p className="text-label mt-1 text-ink-muted">{author.role}</p>
          <p className="measure-prose mt-stack-1 text-body text-ink-muted">{author.bio}</p>
          <ul className="mt-stack-1 flex flex-wrap gap-2">
            {author.beats.map((b) => (
              <li key={b} className="text-label rounded-pill border border-line bg-surface px-2.5 py-1 text-ink-muted">
                {b}
              </li>
            ))}
          </ul>
          {author.email ? (
            <a
              href={`mailto:${author.email}`}
              className="mt-stack-1 inline-flex items-center gap-2 text-small text-green-500 underline underline-offset-4"
            >
              <Mail className="h-4 w-4" aria-hidden />
              {author.email}
            </a>
          ) : null}
        </div>
      </header>

      <section className="mt-stack-3" aria-labelledby="skribent-artiklar">
        <h2 id="skribent-artiklar" className="flex items-center gap-2 text-h2 font-display text-ink">
          <PenLine className="h-5 w-5 text-green-700" aria-hidden />
          Publicerat ({written.length})
        </h2>
        <ul className="mt-stack-2 divide-y divide-line border-y border-line">
          {written.map((p) => (
            <li key={p.slug} className="py-stack-1">
              <Link to="/nyheter/$slug" params={{ slug: p.slug }} className="group block">
                <span className="text-label block text-ink-muted">
                  {CATEGORY_LABELS[p.category]} · {formatDate(p.publishedAt)} · {readingMinutes(p)} min
                </span>
                <span className="mt-1 block text-h4 font-display text-ink group-hover:text-green-700">
                  {p.title}
                </span>
                <span className="mt-1 block text-small text-ink-muted">{p.dek}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
